/**
 * 工具函数 - 设备检测、性能优化
 */

// 检测是否移动端
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
    || window.innerWidth < 768
}

// 检测是否在微信小程序webview中
export function isWeChatMiniProgram() {
  // 检测微信小程序环境
  // 方法1: 检查全局变量
  if (typeof window !== 'undefined') {
    if (window.__wxjs_environment === 'miniprogram') {
      return true
    }
    // 方法2: 检查User-Agent
    if (/miniProgram/i.test(navigator.userAgent)) {
      return true
    }
    // 方法3: 检查wx对象
    if (window.wx && window.wx.miniProgram) {
      return true
    }
  }
  return false
}

// 将Blob转换为Base64
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// 将URL（blob URL或普通URL）转换为Base64
export async function urlToBase64(url) {
  try {
    // 如果是blob URL，先fetch获取blob
    if (url.startsWith('blob:')) {
      const response = await fetch(url)
      const blob = await response.blob()
      return await blobToBase64(blob)
    }
    // 如果是data URL，直接返回
    if (url.startsWith('data:')) {
      return url
    }
    // 如果是普通URL，需要先fetch
    const response = await fetch(url)
    const blob = await response.blob()
    return await blobToBase64(blob)
  } catch (error) {
    console.error('转换URL为Base64失败:', error)
    throw error
  }
}

// uniCloud 云函数上传接口地址（部署后替换为实际地址）
// 在 uniCloud 控制台 -> 云函数 -> uploadImage -> 详情 -> 云函数URL化 中获取
const UPLOAD_API_URL = 'https://fc-mp-4ab7f4a0-3d28-41ee-a661-b1f446732804.next.bspapp.com/uploadImage'

// 通用下载函数，支持普通浏览器和微信小程序webview
export async function downloadFile(url, filename) {
  const isMiniProgram = isWeChatMiniProgram()
  
  if (isMiniProgram) {
    // 微信小程序环境：通过云函数上传，然后跳转到小程序下载
    try {
      // 将URL转换为base64（如果已经是data URL，直接使用）
      const base64 = await urlToBase64(url)
      
      console.log('检测到小程序环境，开始上传到云存储...')
      console.log('Base64 数据长度:', base64.length)
      
      // 调用云函数上传 GIF 到云存储
      const uploadResult = await uploadToCloud(base64, filename || 'image.gif')
      
      if (uploadResult.success && uploadResult.data) {
        console.log('上传成功:', uploadResult.data)
        
        // 通过 postMessage 发送下载 URL 给小程序
        if (window.wx && window.wx.miniProgram) {
          // 存入 globalData
          window.wx.miniProgram.postMessage({
            data: {
              type: 'downloadGif',
              downloadUrl: uploadResult.data.downloadUrl,
              fileID: uploadResult.data.fileID,
              filename: filename || 'image.gif'
            }
          })
          
          // 跳转到小程序下载页面
          setTimeout(() => {
            window.wx.miniProgram.navigateTo({
              url: '/pages/download/gifDownload?downloadUrl=' + encodeURIComponent(uploadResult.data.downloadUrl)
            })
          }, 100)
          
          console.log('已跳转到小程序下载页面')
          return true
        }
      } else {
        throw new Error(uploadResult.error || '上传失败')
      }
    } catch (error) {
      console.error('小程序下载失败:', error)
      // 显示错误提示
      alert('保存失败: ' + (error.message || '请稍后重试'))
      return false
    }
  } else {
    // 普通浏览器环境：使用传统下载方式
    return downloadFileFallback(url, filename)
  }
}

// 上传到云存储（通过云函数 HTTP 接口）
async function uploadToCloud(base64, filename) {
  try {
    console.log('调用云函数上传接口...')
    
    const response = await fetch(UPLOAD_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'upload',
        base64: base64,
        filename: filename,
        type: 'gif'
      })
    })
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const result = await response.json()
    console.log('云函数返回:', result)
    
    return result
  } catch (error) {
    console.error('上传到云存储失败:', error)
    throw error
  }
}

// 传统下载方式（fallback）
export function downloadFileFallback(url, filename) {
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return true
  } catch (error) {
    console.error('下载失败:', error)
    return false
  }
}

// 检测是否支持 OffscreenCanvas
export function supportsOffscreenCanvas() {
  return typeof OffscreenCanvas !== 'undefined'
}

// 获取设备性能配置
export function getDeviceConfig() {
  const mobile = isMobile()
  return {
    isMobile: mobile,
    // 文件大小限制 (字节)
    maxGifSize: mobile ? 15 * 1024 * 1024 : 30 * 1024 * 1024,     // 15MB / 30MB
    maxVideoSize: mobile ? 50 * 1024 * 1024 : 150 * 1024 * 1024,  // 50MB / 150MB
    maxVideoDuration: mobile ? 60 : 120,  // 秒
    // 处理尺寸限制
    maxProcessWidth: mobile ? 640 : 1024,
    maxProcessHeight: mobile ? 640 : 1024,
    // 分批处理
    batchSize: mobile ? 5 : 10,  // 每批处理帧数
    // 质量设置
    defaultQuality: mobile ? 12 : 10,
    defaultFps: mobile ? 12 : 15
  }
}

// 计算压缩后的尺寸（保持宽高比）
export function getScaledSize(width, height, maxWidth, maxHeight) {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height }
  }
  
  const ratio = Math.min(maxWidth / width, maxHeight / height)
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio)
  }
}

// 格式化文件大小
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 检查文件大小
export function checkFileSize(file, maxSize) {
  if (file.size > maxSize) {
    return {
      valid: false,
      message: `文件过大（${formatFileSize(file.size)}），最大支持 ${formatFileSize(maxSize)}`
    }
  }
  return { valid: true }
}

// 延迟执行，让出主线程
export function yieldToMain() {
  return new Promise(resolve => {
    setTimeout(resolve, 0)
  })
}

// 分批处理数组
export async function processBatch(items, batchSize, processor, onProgress) {
  const total = items.length
  let processed = 0
  
  for (let i = 0; i < total; i += batchSize) {
    const batch = items.slice(i, Math.min(i + batchSize, total))
    
    for (const item of batch) {
      await processor(item, processed)
      processed++
      if (onProgress) {
        onProgress(processed, total)
      }
    }
    
    // 每批处理完让出主线程
    await yieldToMain()
  }
}

// 创建可复用的 Canvas（避免频繁创建销毁）
let reusableCanvas = null
let reusableCtx = null

export function getReusableCanvas(width, height) {
  if (!reusableCanvas) {
    reusableCanvas = document.createElement('canvas')
    reusableCtx = reusableCanvas.getContext('2d')
  }
  reusableCanvas.width = width
  reusableCanvas.height = height
  return { canvas: reusableCanvas, ctx: reusableCtx }
}

// 释放 canvas 资源
export function releaseCanvas(canvas) {
  if (canvas) {
    canvas.width = 1
    canvas.height = 1
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.clearRect(0, 0, 1, 1)
    }
  }
}

// 流式处理 GIF 帧（不存储所有帧到内存）
export class StreamGifProcessor {
  constructor(options = {}) {
    this.config = getDeviceConfig()
    this.onProgress = options.onProgress || (() => {})
    this.onFrame = options.onFrame || (() => {})
    this.onComplete = options.onComplete || (() => {})
    this.onError = options.onError || (() => {})
  }
  
  // 获取缩放尺寸
  getProcessSize(originalWidth, originalHeight) {
    return getScaledSize(
      originalWidth, 
      originalHeight, 
      this.config.maxProcessWidth, 
      this.config.maxProcessHeight
    )
  }
}

export default {
  isMobile,
  isWeChatMiniProgram,
  supportsOffscreenCanvas,
  getDeviceConfig,
  getScaledSize,
  formatFileSize,
  checkFileSize,
  yieldToMain,
  processBatch,
  getReusableCanvas,
  releaseCanvas,
  StreamGifProcessor,
  blobToBase64,
  urlToBase64,
  downloadFile,
  downloadFileFallback
}

